import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Label } from 'recharts';
import { motion } from 'framer-motion';
import { useTheme } from '@emotion/react';

export default function HourlyPerformanceChart({ data }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // X-axis labels: rightmost is 'Now', then '1', '2', ..., '23' (hours ago)
  const xLabels = data ? data.map((item, i) => {
    if (i === data.length - 1) return 'Now';
    return item.hoursAgo ? item.hoursAgo.toString() : (data.length - 1 - i).toString();
  }) : [];

  const renderCustomTick = (props) => {
    const { x, y, payload } = props;
    const label = xLabels[payload.value];
    const isNow = label === 'Now';
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fontWeight={isNow ? 700 : 400}
          fill={isNow ? (isDark ? '#fbbf24' : '#f59e42') : (isDark ? '#d1d5db' : '#374151')}
          fontSize={14}
        >
          {label}
        </text>
      </g>
    );
  };

  // Custom tooltip for best-practice UX
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const calls = payload[0].value;
      const isCurrentHour = dataPoint.idx === data.length - 1;
      
      return (
        <div style={{ 
          background: isDark ? '#1f2937' : '#fff', 
          color: isDark ? '#fff' : '#000', 
          borderRadius: 14, 
          boxShadow: '0 2px 12px #0002', 
          padding: 16, 
          border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', 
          minWidth: 160 
        }}>
          <div className="font-semibold mb-2">
            {isCurrentHour ? 'Current hour (so far)' : `${dataPoint.hoursAgo} hours ago`}
          </div>
          <div className="text-sm text-gray-400 mb-1">
            {dataPoint.hourLabel || `${dataPoint.hour}:00`}
          </div>
          <div>Calls: <b>{calls}</b></div>
        </div>
      );
    }
    return null;
  };

  // Attach idx to each data point for tooltip
  const chartData = data ? data.map((d, i) => ({ ...d, idx: i })) : [];

  if (!data || data.length === 0 || data.every(d => d.volume === 0)) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl shadow-xl p-6 flex items-center justify-center min-h-[220px] transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-white'}`}
      >
        <span className="text-gray-400 text-lg">No call activity in the last 24 hours.</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl shadow-xl p-6 transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-white'}`}
    >
      <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-2">
        <div>
          <h2 className={`text-xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>24-Hour Call Volume</h2>
          <span className="text-gray-400 text-sm">Hourly call distribution (last 24h)</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 30 }}>
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={isDark ? '#60A5FA' : '#3B82F6'} stopOpacity={0.9}/>
              <stop offset="100%" stopColor={isDark ? '#1e293b' : '#dbeafe'} stopOpacity={0.2}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
          <XAxis
            dataKey="idx"
            stroke={isDark ? "#d1d5db" : "#374151"}
            tick={renderCustomTick}
            interval={0}
            minTickGap={0}
            height={40}
          >
            <Label value="Hour" offset={-10} position="insideBottom" style={{ fill: isDark ? '#d1d5db' : '#374151', fontSize: 14 }} />
          </XAxis>
          <YAxis stroke={isDark ? "#d1d5db" : "#374151"} allowDecimals={false} tick={{ fontSize: 12 }}>
            <Label value="Calls" angle={-90} position="insideLeft" style={{ fill: isDark ? '#d1d5db' : '#374151', fontSize: 13 }} />
          </YAxis>
          <Tooltip content={<CustomTooltip />} cursor={{ fill: isDark ? '#33415522' : '#e0e7ef22' }} />
          <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ color: isDark ? '#fff' : '#000' }} />
          <Bar
            dataKey="volume"
            name="Call Volume"
            fill="url(#barGradient)"
            radius={[8, 8, 0, 0]}
            isAnimationActive={true}
            animationDuration={1200}
            {
              ...{
                shape: (props) => {
                  const { x, y, width, height, payload } = props;
                  const highlight = payload.idx === data.length - 1;
                  return (
                    <g>
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        rx={highlight ? 14 : 8}
                        fill={highlight ? (isDark ? '#f59e42' : '#fbbf24') : 'url(#barGradient)'}
                        stroke={highlight ? (isDark ? '#fbbf24' : '#f59e42') : 'none'}
                        strokeWidth={highlight ? 2 : 0}
                        opacity={highlight ? 1 : 0.95}
                        style={highlight ? { filter: 'drop-shadow(0 0 8px #fbbf24aa)' } : {}}
                      />
                    </g>
                  );
                }
              }
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </motion.div>
  );
} 